import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';

import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import Checkbox from '@lib/inputs/Checkbox';
import Input from '@lib/inputs/Input';
import PhoneNumberInput from '@lib/inputs/PhoneNumberInput';

import { GridStyledWrapper, StyledDeclarationContainer } from '../voterRegistration.styled';
import { VoterRegistrationFormError } from '../VoterRegistrationFormError';
import { IVoterRegistrationFormValues } from '../voterRegistrationTypes';

function VotingFormHelpSection() {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext();
    const intl = useIntl();

    const handleFieldChange = (name: string, value: string) => {
        setValue(name as keyof IVoterRegistrationFormValues, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const name = watch('helpWithForm.name');
    const address = watch('helpWithForm.address');
    const phone = watch('helpWithForm.phone');
    const confirmation = watch('helpWithForm.confirmation');

    const isCheckboxRequired = !!name || !!address || !!phone;

    return (
        <div id="helpWithForm-section">
            <FormattedMessage id="voterRegistration.form.votingFormHelp.header" tagName="h2" />
            <AlertMessage
                type={'info'}
                content={<FormattedMessage id="voterRegistration.form.votingFormHelp.alert" />}
                showIcon={true}
                icon={'info-circle'}
            />
            <GridStyledWrapper>
                <div className="fullWidth">
                    <Input
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.votingFormHelp.name' }),
                        }}
                        inputData={{
                            id: 'helpWithForm.name',
                            name: 'helpWithForm.name',
                            isInputRequired: confirmation,
                            value: watch('helpWithForm.name'),
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('helpWithForm.name', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="helpWithForm.name"
                        hasError={!!errors?.helpWithForm?.name}
                        messageId={errors?.helpWithForm?.name?.message}
                    />
                </div>
                <div className="fullWidth">
                    <Input
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.votingFormHelp.address' }),
                        }}
                        inputData={{
                            id: 'helpWithForm.address',
                            name: 'helpWithForm.address',
                            isInputRequired: confirmation,
                            value: watch('helpWithForm.address'),
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('helpWithForm.address', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="helpWithForm.address"
                        hasError={!!errors?.helpWithForm?.address}
                        messageId={errors?.helpWithForm?.address?.message}
                    />
                </div>
                <div className="fullWidth">
                    <PhoneNumberInput
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.votingFormHelp.phone' }),
                        }}
                        inputData={{
                            id: 'helpWithForm.phone',
                            name: 'helpWithForm.phone',
                            isInputRequired: confirmation,
                            type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                            validationType: VALIDATION_TYPES.PHONE_NUMBER,
                            helperClasses: [],
                            value: watch('helpWithForm.phone'),
                            mask: true,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('helpWithForm.phone', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="helpWithForm.phone"
                        hasError={!!errors?.helpWithForm?.phone}
                        messageId={errors?.helpWithForm?.phone?.message}
                    />
                </div>
            </GridStyledWrapper>

            <StyledDeclarationContainer>
                <p>
                    <FormattedMessage
                        id="voterRegistration.form.votingFormHelp.helpWithForm.alert.header"
                        tagName="strong"
                    />
                </p>
                <p>
                    <FormattedMessage
                        id="voterRegistration.form.votingFormHelp.helpWithForm.alert.title"
                        values={{
                            strong: (chunks: any) => <strong>{chunks}</strong>,
                        }}
                    />
                </p>
                <ul className="usa-list">
                    <FormattedMessage
                        id="voterRegistration.form.votingFormHelp.helpWithForm.alert.list.1"
                        tagName="li"
                    />
                    <FormattedMessage
                        id="voterRegistration.form.votingFormHelp.helpWithForm.alert.list.2"
                        tagName="li"
                    />
                </ul>
                <div className="margin-2">
                    <Checkbox
                        label={{
                            text: (
                                <FormattedMessage id="voterRegistration.form.votingFormHelp.helpWithForm.alert.checkbox" />
                            ),
                            labelClass: [`required-${isCheckboxRequired}`],
                        }}
                        inputData={{
                            id: 'helpWithForm.confirmation',
                            name: 'helpWithForm.confirmation',
                            isInputRequired: true,
                            checked: watch('helpWithForm.confirmation'),
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('helpWithForm.confirmation', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="helpWithForm.confirmation"
                        hasError={!!errors?.helpWithForm?.confirmation}
                        messageId={errors?.helpWithForm?.confirmation?.message}
                    />
                </div>
            </StyledDeclarationContainer>
        </div>
    );
}

export default VotingFormHelpSection;
